import {ApiError} from '../shared/data/api-error';
import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {LabOrderBusinessService} from '../shared/lab-order-business.service';
import {FormGroup, FormBuilder, FormControl, Validators} from '@angular/forms';

import {LabOrder} from '../shared/data/lab-order';
import {UtilsService} from '../shared/utils.service';
import {HttpErrorResponse} from '@angular/common/http';
import {Subscription} from 'rxjs/Subscription';
import {LabCodeMapperService} from '../lab-code-mapper/lab-code-mapper.service';
import {SubscriptionsContainer} from '../shared/subscriptions-container';
import {LensCatalogBusinessService} from '../shared/lens-catalog-business.service';
import {LensCatalogReferenceType} from '../shared/data/lens-catalog-api-item';
import {BillingOptionCode} from '../shared/data/billing-option-code';

@Component({
  selector: 'app-modify-order',
  templateUrl: './modify-order.component.html',
  styleUrls: ['./modify-order.component.css']
})
export class ModifyOrderComponent implements OnInit, OnDestroy {
  labOrderModifyForm: FormGroup;
  labOrderNumber: any;
  labInvoiceNumber: any;
  labOrderUrl: string;
  subs = new SubscriptionsContainer();

  @Input() labOrder: LabOrder;
  routeSubscription: Subscription;
  messages: string[] = [];


  constructor(
    private route: ActivatedRoute,
    private labOrderBusinessService: LabOrderBusinessService,
    private labCodeMapperService: LabCodeMapperService,
    private lensCatalogBusinessService: LensCatalogBusinessService,
    private router: Router,
    private formBuilder: FormBuilder
  ) {
  }

  ngOnInit(): void {

    this.labOrderModifyForm = this.formBuilder.group({});

    // subscribe to the path params
    this.subs.add = this.route.params.subscribe(params => {
      this.labOrderNumber = params['labOrderNumber'];
      this.labOrderUrl = params['labOrderUrl'];
    });

    // subscribe to the query params
    this.subs.add = this.route.queryParams.subscribe(queryParams => {
      // it is optional parameter
      if (queryParams['labInvoiceNumber']) {
        this.labInvoiceNumber = queryParams['labInvoiceNumber'];
        console.log('Received query parameter labInvoiceNumber: ', this.labInvoiceNumber);
      }
    });

    // view lab order via search results link.
    if (!UtilsService.isEmpty(this.labOrderUrl)) {
      console.log('Received LabOrderUrl as parameter: ', this.labOrderUrl);

      this.subs.add = this.labOrderBusinessService.viewLabOrderByUrl(this.labOrderUrl)
        .subscribe(
          (labOrder) => {
            console.log('Retrieved order: ' + labOrder.labOrderNumber);
            this.labOrder = labOrder;
            this.labOrder.prescription.sphereLeft = UtilsService.formatRxPower(this.labOrder.prescription.sphereLeft);
            this.labOrder.prescription.sphereRight = UtilsService.formatRxPower(this.labOrder.prescription.sphereRight);
            this.labOrder.prescription.cylinderLeft = UtilsService.formatRxPower(this.labOrder.prescription.cylinderLeft);
            this.labOrder.prescription.cylinderRight = UtilsService.formatRxPower(this.labOrder.prescription.cylinderRight);
          },
          (error) => {
            this.labOrder = null;
            this.messages = ['Unable to retrieve order by url \'' + this.labOrderUrl + '\''];
            console.error(this.messages);
            this.router.navigate(['orders', {message: this.messages}]);
          });
      // view lab order via modify order link.
    } else if (!UtilsService.isEmpty(this.labOrderNumber)) {
      console.log('Received LabOrderNumber as parameter: ', this.labOrderNumber);

      this.subs.add = this.labOrderBusinessService.viewLabOrder(this.labOrderNumber)
        .subscribe(
          (labOrder) => {
            console.log('Retrieved order: ' + labOrder.labOrderNumber);
            this.labOrder = labOrder;
            this.labOrder.prescription.sphereLeft = UtilsService.formatRxPower(this.labOrder.prescription.sphereLeft);
            this.labOrder.prescription.sphereRight = UtilsService.formatRxPower(this.labOrder.prescription.sphereRight);
            this.labOrder.prescription.cylinderLeft = UtilsService.formatRxPower(this.labOrder.prescription.cylinderLeft);
            this.labOrder.prescription.cylinderRight = UtilsService.formatRxPower(this.labOrder.prescription.cylinderRight);
          },
          (error) => {
            this.labOrder = null;
            this.messages = ['Unable to retrieve order \'' + this.labOrderNumber + '\''];
            console.error(this.messages);
            this.router.navigate(['orders', {message: this.messages}]);
          });
    } else {
      this.messages = ['No LabOrderUrl or LabOrderNumber parameter provided to modify.'];
      console.error(this.messages);
      this.router.navigate(['orders', {message: this.messages}]);
    }
  }

  ngOnDestroy(): void {
    console.log(' Modify Order Component - ngOnDestroy ');
    this.subs.dispose();
  }

  onModify() {
    console.log('modify');


    // Check to make sure tint percentage should integer only
    if (this.validate()) {
      // reset messages
      this.messages = [];
      const labOrderNumber = this.labOrder.labOrderNumber;
      this.lensCatalogBusinessService.getLensDetail(this.labOrder).subscribe(
        lensCatalogReferenceType => {
          if (lensCatalogReferenceType !== null ) {
            this.refreshBillingOptionCodes(lensCatalogReferenceType);
          }

          this.subs.add = this.labOrderBusinessService.modifyLabOrder(this.labOrder, this.labInvoiceNumber)
            .subscribe(
              (labOrder) => {
                console.log('Successfully modified order: ' + labOrderNumber);
                this.router.navigate(['viewOrder/', labOrderNumber]);
              },
              (error) => {
                // show modify lab-order warning messages
                if (error.status === 400) {
                  this.messages = this.processErrorMessages(error);
                  console.warn('Modify order \'' + labOrderNumber + '\' warnings: ' + JSON.stringify(error));
                } else {
                  this.messages = ['Error while modifying order \'' + labOrderNumber + '\''];
                  console.error(this.messages + ': ' + JSON.stringify(error));
                }
                alert('Something went wrong when trying to save this order.');
              }
            );
        },
        (error) => {
          console.log('Unable to get Billing Option from Lens Catalog.');
        }
      );
    } else {
      alert('Something went wrong when trying to save this order.');
    }

  }

  private processErrorMessages(httpError: HttpErrorResponse): string[] {

    const errorMessages: string[] = [];
    if (httpError.error) {
      const errors: ApiError[] = <ApiError[]>httpError.error;
      for (let i = 0; i < errors.length; i++) {
        errorMessages.push(errors[i].message);
      }
    }
    return errorMessages;
  }

  validate() {
    this.messages = [];
    let passed = true;
    const tintPercentage = this.labOrder.lens.tintSamplePercentage;
    if (typeof tintPercentage === 'string' && Number.isNaN(Number(tintPercentage))) {
      this.messages.push('Tint Percentage must be an integer.');
      passed = false;
    }
    if (!this.labOrder.lens.spectacleLensId || this.labOrder.lens.spectacleLensId.trim().length === 0) {
      this.messages.push('Lens Style is required');
      passed = false;
    }
    return passed;
  }

   refreshBillingOptionCodes(lensCatalogReferenceType: LensCatalogReferenceType) {
    const billingOptionCodes = [];
    const allFormularyEnhancements = lensCatalogReferenceType.spectacleLensFormularyAssessment.allFormularyEnhancements;
    if (allFormularyEnhancements) {
      allFormularyEnhancements.forEach(billingOptionCode => {
          billingOptionCodes.push({id: lensCatalogReferenceType.externalId, code: billingOptionCode.code,
            type: BillingOptionCode.TYPE_LENS});
        }
      );
  }
     // Overwrite the old billing options codes.
     this.labOrder.lens.billingOptionCodes = billingOptionCodes;
  }
}



// WEBPACK FOOTER //
// /home/jenkins-agent/workspace/ng-labportal-sandbox-build/src/app/modify-order/modify-order.component.ts